/**
 * ReferralQRModal — turns a referral code into a scannable QR code for marketing.
 *
 * The QR encodes the same registration link the "Copy Link" buttons share
 * (origin + /register?ref=CODE), so a scan drops the visitor straight onto the
 * signup page with the referral attributed. Affiliates and admins can preview,
 * download a print-ready PNG, or copy the link.
 *
 * Used by both the affiliate dashboard and the admin user drawer.
 */
import { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'react-toastify';

export function referralLink(code) {
  return window.location.origin + '/register?ref=' + code;
}

export default function ReferralQRModal({ code, label, onClose }) {
  const canvasWrapRef = useRef(null);
  const link = referralLink(code);

  const getCanvas = () => canvasWrapRef.current?.querySelector('canvas');

  const download = () => {
    const canvas = getCanvas();
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `ab8beauty-referral-${code}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('QR code downloaded');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gradient-to-r from-tea-50 to-white">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-tea-600 font-bold">Referral QR code</div>
            <div className="font-bold text-gray-900 font-mono">{code}</div>
            {label && <div className="text-xs text-gray-500">{label}</div>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        <div className="p-6 flex flex-col items-center">
          <div ref={canvasWrapRef} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <QRCodeCanvas
              value={link}
              size={240}
              level="M"
              marginSize={2}
              fgColor="#15803d"
              bgColor="#ffffff"
            />
          </div>
          <p className="text-xs text-gray-500 mt-4 text-center break-all">{link}</p>
          <p className="text-[11px] text-gray-400 mt-1 text-center">
            Scan to sign up with this referral attributed. Add it to flyers, packaging, or social posts.
          </p>
        </div>

        <div className="px-6 pb-6 grid grid-cols-2 gap-2">
          <button
            onClick={copyLink}
            className="text-sm font-semibold px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
            Copy link
          </button>
          <button
            onClick={download}
            className="text-sm font-bold px-4 py-2.5 rounded-lg bg-tea-600 text-white hover:bg-tea-700">
            Download PNG
          </button>
        </div>
      </div>
    </div>
  );
}
